import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useEffect } from 'react';
import { redirect } from 'react-router-dom';
import { Subject } from 'rxjs';
import { match } from 'ts-pattern';
import { useSubscription } from '../components/hooks/useSubscription';
import { serverUrl } from '../config';
import { routes } from '../data/routes';

const getNewSession = async (token: string): Promise<Session> =>
  fetch(`${serverUrl}/api/actions/refresh-session`, {
    method: 'post',
    headers: { authorization: `Token ${token}` },
  }).then((res) => res.json());

export type Session = {
  email: string;
  firstName: string;
  lastName: string;
  id: string;
  token: string;
  expiry: string;
};

// --- Atoms ------------------------------------------------------------------
const rawSessionAtom = atomWithStorage<Session | undefined>('session', undefined);
export const sessionAtom = atom<Session | undefined>((get) => {
  const rawSession = get(rawSessionAtom);
  if (!rawSession) return undefined;

  if (new Date(rawSession.expiry) < new Date()) {
    return undefined;
  } else {
    return get(rawSessionAtom);
  }
});

export const tokenAtom = atom<string | undefined>((get) => get(sessionAtom)?.token);

export const authedAtom = atom<boolean>((get) => !!get(sessionAtom));

// --- Bus --------------------------------------------------------------------
type SessionEvent = { type: 'log-in'; session: Session } | { type: 'log-out' };

const sessionBus$ = atom(new Subject<SessionEvent>());

export const useSessionHandler = () => {
  const bus$ = useAtomValue(sessionBus$);
  const setSession = useSetAtom(rawSessionAtom);

  useSubscription(() => {
    bus$.subscribe((event) => {
      match(event)
        .with({ type: 'log-in' }, ({ session }) => {
          setSession(session);
        })
        .with({ type: 'log-out' }, () => {
          redirect(routes.logIn);
          // TODO: This is a HACK to make sure the redirect happens before the session is cleared
          // The problem this solves is that all the page queries throw errors when the session is cleared
          setTimeout(() => setSession(undefined), 1);
        })
        .exhaustive();
    });
  }, [bus$, setSession]);
};

export const useSessionRefresh = () => {
  const bus$ = useAtomValue(sessionBus$);
  const currentSession = useAtomValue(sessionAtom);

  useEffect(() => {
    if (!currentSession) return;

    const interval = setInterval(async () => {
      const session = await getNewSession(currentSession.token);
      bus$.next({ type: 'log-in', session });
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [bus$, currentSession]);
};

export const useSession = () => {
  const bus$ = useAtomValue(sessionBus$);
  const dispatch = (event: SessionEvent) => bus$.next(event);

  return {
    logIn: (session: Session) => dispatch({ type: 'log-in', session }),
    logOut: () => dispatch({ type: 'log-out' }),
  };
};

export const useToken = () => {
  const token = useAtomValue(tokenAtom);
  return token;
};
